import createClient, { type Middleware } from "openapi-fetch";
import {
  getAccessToken,
  notifySessionLost,
  setAccessToken,
  setImpersonatedBy,
} from "@/auth/tokens";
import type { paths } from "./schema";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Endpoints que nunca levam Bearer e nunca disparam refresh.
 * Tentar renovar em cima de um 401 destes causaria laço infinito.
 */
const PUBLIC_PATHS = [
  "/auth/login",
  "/auth/signup",
  "/auth/google",
  "/auth/refresh",
  "/auth/logout",
  "/auth/password/forgot",
  "/auth/password/reset",
  "/health",
  // Catálogo público, consumido pela tela de cadastro (sem sessão).
  "/verticals",
];

function isPublicPath(url: string): boolean {
  const path = new URL(url, API_BASE_URL).pathname;
  return PUBLIC_PATHS.some((p) => path.endsWith(p));
}

/**
 * Guarda uma cópia intocada de cada requisição para poder reenviá-la depois
 * de um refresh — o corpo do Request original já foi consumido pelo fetch.
 */
const pristine = new WeakMap<Request, Request>();

/**
 * Desfecho de uma tentativa de renovar a sessão.
 *
 * A distinção entre `no_session` e `unreachable` é a razão de existir deste
 * tipo: um `null` para os dois casos fazia o app deslogar o dono sempre que
 * o servidor estivesse fora do ar — ele recarregava a página, o refresh
 * falhava por falta de resposta, e isso era lido como "a sessão acabou".
 *
 * A regra é estreita de propósito, e o erro perigoso tem direção: tratar um
 * 401 de verdade como `unreachable` deixaria o painel aberto sem sessão. Só
 * conta como `unreachable` o que o servidor NÃO respondeu — `fetch` que
 * estoura (offline, DNS, porta fechada) e 5xx. Qualquer veredito que ele
 * tenha dado sobre a credencial continua sendo sessão morta.
 */
export type RefreshOutcome =
  | { status: "renewed"; token: string }
  | { status: "no_session" }
  | { status: "unreachable" };

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Renova a sessão. Chamadas simultâneas compartilham a mesma promessa: sem
 * isso, cinco requisições expirando juntas viram cinco POSTs de refresh, e
 * o backend rotaciona o cookie a cada um deles.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh(): Promise<RefreshOutcome> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      // Sem isto o cookie não vai nem volta, e o sintoma é o app deslogando
      // sozinho a cada reload.
      credentials: "include",
    });
  } catch {
    // `fetch` só estoura quando a resposta não chegou. Isso não diz nada
    // sobre a sessão — o cookie continua no navegador.
    return { status: "unreachable" };
  }

  // 5xx é o servidor no ar mas quebrado: também não é veredito sobre a
  // credencial. 401/403 é.
  if (response.status >= 500) return { status: "unreachable" };
  if (!response.ok) return { status: "no_session" };

  try {
    const body = (await response.json()) as {
      data: { access_token: string; impersonation?: { actor: string } };
    };
    setAccessToken(body.data.access_token);
    // Sessão aberta pelo painel interno em nome do dono. Vem no corpo
    // justamente para o front não precisar ler claim de JWT.
    setImpersonatedBy(body.data.impersonation?.actor ?? null);
    return { status: "renewed", token: body.data.access_token };
  } catch {
    // 200 com corpo ilegível é servidor quebrado, não sessão expirada.
    return { status: "unreachable" };
  }
}

/**
 * `fetch` autenticado para o que o cliente tipado não sabe mandar — hoje só
 * o upload da logo, que é `multipart/form-data` com um arquivo, e o
 * openapi-fetch serializa corpo como JSON.
 *
 * Repete de propósito o pouco que o `authMiddleware` faz: Bearer na ida e,
 * num 401, um refresh compartilhado (`refreshSession`) seguido de UMA nova
 * tentativa. Sem isso, subir uma imagem com o access token recém-vencido
 * derrubaria a sessão do dono no meio de um upload que teria funcionado.
 *
 * A repetição é uma só e nunca vira laço: a segunda tentativa já vai com o
 * token novo, e um 401 nela é sessão morta de verdade.
 */
export async function authedFetch(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // `credentials: include` também aqui: é o que leva o cookie de refresh.
    return fetch(url, { ...init, headers, credentials: "include" });
  };

  const response = await send(getAccessToken());
  if (response.status !== 401) return response;

  const outcome = await refreshSession();
  if (outcome.status !== "renewed") {
    if (outcome.status === "no_session") notifySessionLost();
    return response;
  }
  return send(outcome.token);
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    if (!isPublicPath(request.url)) {
      const token = getAccessToken();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      pristine.set(request, request.clone());
    }
    return request;
  },

  async onResponse({ request, response }) {
    if (response.status !== 401 || isPublicPath(request.url)) return response;

    const retryable = pristine.get(request);
    if (!retryable) return response;
    pristine.delete(request);

    const outcome = await refreshSession();
    if (outcome.status !== "renewed") {
      // Servidor que caiu entre o 401 e o refresh não derruba a sessão: o
      // cookie continua válido e a próxima tentativa reidrata.
      if (outcome.status === "no_session") notifySessionLost();
      return response;
    }

    const headers = new Headers(retryable.headers);
    headers.set("Authorization", `Bearer ${outcome.token}`);
    return fetch(new Request(retryable, { headers }));
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  // Vale para toda chamada: o cookie de refresh só viaja com isto ligado.
  credentials: "include",
  // Resolvido a cada chamada em vez de capturado agora: sem isto o cliente
  // congela a referência de fetch na importação e fica impossível de testar.
  fetch: (request) => globalThis.fetch(request),
});

api.use(authMiddleware);
